import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { ticketDetailedSourcesLabel } from "helpers/tickets";
import { DetailedSource } from "interfaces/entities/ticket";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { selectAvailableSources } from "store/entities/license";
import { IRuleTriggerDetailedSourceType } from "store/entities/rules/interfaces";

import { changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerDetailedSourceType>;
}

export const SourceTypeTrigger: FC<IProps> = ({ input }) => {
  const availableSources = useSelector(selectAvailableSources);

  const equalityOptions = [createEqualityOption("is"), createEqualityOption("isNot")];

  const selectedEqualityOptions = [createEqualityOption(input.value.invert ? "isNot" : "is")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];
    const newInvert = selectedOption.key === "isNot";
    changeTriggerValues(input, { invert: newInvert });
  };

  const sourceOptions = availableSources.map(createSourceOption);

  const selectedSourceOptions = input.value.in.map(source => createSourceOption(source));

  const handleSelectSource = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    const newSources = selectedOptions.map(opt => opt.key as DetailedSource);
    changeTriggerValues(input, { in: newSources });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={sourceOptions}
        selected={selectedSourceOptions}
        onSelect={handleSelectSource}
        searchDisabled
        placeholder="Select source…"
      />
    </>
  );
};

const createEqualityOption = (key: "isNot" | "is"): IPickerListItem => ({
  key,
  name: key === "isNot" ? "is not" : "is",
});

const createSourceOption = (source: DetailedSource): IPickerListItem => ({
  key: source,
  name: ticketDetailedSourcesLabel[source],
});
